import { Drawer, IconButton, InputAdornment, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ClearIcon from '@material-ui/icons/Clear';
import RemoveIcon from '@material-ui/icons/Remove';
import { KeyboardEvent, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatbotSession } from '../../services/ai.service';
import ImgIADrawer from '@orientaction/Asset/Ressources/circle-chatbot-conseiller.png';

const useStyles = makeStyles({
  rootStyle: {
    background: 'rgba(0, 10, 140, 0.42)',
  },
  drawerContent: {
    width: '100%',
    height: '100%',
    left: 'auto',
    display: 'flex',
    flexDirection: 'column',
    '@media(min-width: 960px)': {
      width: '430px',
      height: '100vh',
    },
  },
  header: {
    padding: '16px 20px',
    borderBottom: '1px solid #EDEEF2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
  },
  closeBtn: {
    padding: 8,
    width: 40,
    height: 40,
  },
  title: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '100%',
    color: '#0E1247',
    flex: 1,
    textAlign: 'left',
  },
  chatContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px 20px',
    display: 'flex',
    flexDirection: 'column-reverse',
    gap: '16px',
    backgroundColor: '#FFFFFF',
  },
  welcomeSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  welcomeText: {
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '160%',
    color: '#0E1247',
  },
  predefinedQueriesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  predefinedQueryButton: {
    padding: '14px 16px',
    borderRadius: '20px',
    backgroundColor: '#5763FF',
    color: '#FFFFFF',
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '160%',
    textAlign: 'left',
    cursor: 'pointer',
    border: 'none',
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    whiteSpace: 'pre-line',
    '&:hover': {
      backgroundColor: '#4651E6',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(87, 99, 255, 0.3)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  predefinedQueryTitle: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '160%',
    color: '#FFFFFF',
  },
  predefinedQuerySubtitle: {
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '160%',
    color: '#FFFFFF',
  },
  messagesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  messageContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  messageSender: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '100%',
    color: '#0E1247',
  },
  messageTime: {
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '100%',
    color: '#9799B1',
  },
  messageBubble: {
    padding: '14px 16px',
    borderRadius: '20px',
    maxWidth: '85%',
    wordWrap: 'break-word',
  },
  userMessage: {
    backgroundColor: '#5763FF',
    color: '#FFFFFF',
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '160%',
    alignSelf: 'flex-end',
    marginLeft: 'auto',
  },
  assistantMessage: {
    backgroundColor: '#EDEEF2',
    color: '#0E1247',
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '160%',
    alignSelf: 'flex-start',
    '& p': {
      margin: '0 0 12px 0',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& strong': {
      fontWeight: 600,
    },
    '& ul, & ol': {
      margin: '8px 0',
      paddingLeft: '24px',
    },
    '& li': {
      marginBottom: '4px',
    },
    '& h1, & h2, & h3, & h4': {
      fontWeight: 600,
      margin: '12px 0 8px 0',
    },
  },
  typingIndicator: {
    backgroundColor: '#EDEEF2',
    color: '#0E1247',
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: '14px',
    padding: '12px 16px',
    paddingTop: '17px',
    borderRadius: '8px',
    maxWidth: '80px',
    alignSelf: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    '& span': {
      animation: '$blink 1.4s infinite',
      fontSize: '20px',
      lineHeight: '1',
      '&:nth-child(2)': {
        animationDelay: '0.2s',
      },
      '&:nth-child(3)': {
        animationDelay: '0.4s',
      },
    },
  },
  '@keyframes blink': {
    '0%, 60%, 100%': {
      opacity: 0.3,
    },
    '30%': {
      opacity: 1,
    },
  },
  inputContainer: {
    borderTop: '1px solid #EDEEF2',
    backgroundColor: '#FFFFFF',
    flexShrink: 0,
    position: 'relative',
  },
  input: {
    position: 'relative',
    '& .MuiOutlinedInput-root': {
      borderRadius: '24px',
      backgroundColor: '#FFFFFF',
      alignItems: 'flex-start',
      paddingRight: '50px !important',
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: 'none',
      },
    },
    '& .MuiOutlinedInput-input': {
      fontFamily: 'Poppins',
      fontSize: '14px',
      color: '#0E1247',
      padding: '12px 16px !important',
      paddingRight: '0 !important',
      overflow: 'auto',
      resize: 'none',
      '&::placeholder': {
        color: '#9799B1',
        opacity: 1,
      },
    },
    '& .MuiInputAdornment-root': {
      position: 'absolute',
      right: '8px',
      margin: 0,
      height: 'auto',
    },
  },
  sendButton: {
    padding: 8,
    backgroundColor: '#5763FF',
    color: '#FFFFFF',
    width: 36,
    height: 36,
    '&:hover': {
      backgroundColor: '#4651E6',
    },
    '&:disabled': {
      backgroundColor: '#EDEEF2',
      color: '#9799B1',
    },
  },
  historyLink: {
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '14px',
    color: '#0E1247',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  historyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '0 20px',
  },
  historyItem: {
    padding: '16px',
    backgroundColor: '#F8FAFF',
    border: '1px solid #EDEEF2',
    borderRadius: '8px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#E0E3FF',
      borderColor: '#5763FF',
    },
  },
  historyItemDate: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '14px',
    color: '#0E1247',
    marginBottom: '4px',
  },
  historyItemTitle: {
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: '12px',
    color: '#9799B1',
  },
});

interface IADrawerProps {
  open: boolean;
  onMinimize: () => void;
  onClose: () => void;
  messages: Array<{
    id: string;
    text: string;
    sender: 'user' | 'assistant';
    timestamp: Date;
  }>;
  inputValue: string;
  onInputChange: (value: string) => void;
  onSendMessage: () => void;
  onSelectPredefinedQuery: (query: string) => void;
  isLoading?: boolean;
  viewMode: 'main' | 'history' | 'conversation';
  chatbotHistory: ChatbotSession[];
  isLoadingHistory: boolean;
  onShowHistory: () => void;
  onBackToMain: () => void;
  onLoadConversation: (chatbotId: number) => void;
}

const IADrawer = ({
  open,
  onMinimize,
  onClose,
  messages,
  inputValue,
  onInputChange,
  onSendMessage,
  onSelectPredefinedQuery,
  isLoading = false,
  viewMode,
  chatbotHistory,
  isLoadingHistory,
  onShowHistory,
  onBackToMain,
  onLoadConversation,
}: IADrawerProps) => {
  const classes = useStyles();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);

  const predefinedQueries = [
    'Je rencontre un problème\nJe veux trouver une solution',
    "Je ressens des émotions fortes\nJ'ai besoin d'aide pour mieux les gérer",
    "J'ai besoin qu'on m'aide\nJe veux comprendre une situation compliquée",
  ];

  useEffect(() => {
    if (viewMode === 'history') {
      return;
    }

    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ block: 'start' });
    } else if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = 0;
    }
  }, [messages, viewMode]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputValue.trim()) {
        onSendMessage();
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })} à ${date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  };

  return (
    <Drawer
       anchor="bottom"
      open={open}
      onClose={(event, reason) => {
        // Only allow closing via escape key, not backdrop click
        if (reason !== 'backdropClick') {
          onMinimize();
        }
      }}
      classes={{ root: classes.rootStyle, paper: classes.drawerContent }}
      ModalProps={{
        disableBackdropClick: true,
      }}>
      <div className={classes.header}>
        {viewMode === 'history' ? (
          <>
            <div className={classes.headerRow}>
              <IconButton onClick={onBackToMain}>
                <ArrowBackIcon />
              </IconButton>
              <Typography className={classes.title}>Historique</Typography>
            </div>
            <IconButton onClick={onClose}>
              <ClearIcon />
            </IconButton>
          </>
        ) : viewMode === 'conversation' ? (
          <>
            <div className={classes.headerRow}>
              <IconButton onClick={onBackToMain}>
                <ArrowBackIcon />
              </IconButton>
              <Typography className={classes.title}>Votre coach Orientaction</Typography>
            </div>
            <IconButton onClick={onClose}>
              <ClearIcon />
            </IconButton>
          </>
        ) : (
          <>
            <div className={classes.headerContent}>
              <div className={classes.headerRow}>
                <Typography className={classes.title}>Votre coach Orientaction</Typography>
                <IconButton onClick={onMinimize}>
                  <RemoveIcon />
                </IconButton>
                <IconButton onClick={onClose}>
                  <ClearIcon />
                </IconButton>
              </div>
              {chatbotHistory.length > 0 && (
                <Typography className={classes.historyLink} onClick={onShowHistory}>
                  Voir l&apos;historique
                </Typography>
              )}
            </div>
          </>
        )}
      </div>

      <div className={classes.chatContainer} ref={chatContainerRef}>
        {viewMode === 'history' ? (
          <div className={classes.historyList}>
            {isLoadingHistory ? (
              <Typography style={{ textAlign: 'center', color: '#9799B1' }}>
                Chargement...
              </Typography>
            ) : chatbotHistory.length === 0 ? (
              <Typography style={{ textAlign: 'center', color: '#9799B1' }}>
                Aucun historique
              </Typography>
            ) : (
              chatbotHistory.map((session, index) => (
                <div
                  key={session.id}
                  className={classes.historyItem}
                  onClick={() => onLoadConversation(session.id)}>
                  <Typography className={classes.historyItemDate}>
                    {session.title || '-'}
                  </Typography>
                  <Typography className={classes.historyItemTitle}>
                    {formatDate(session.createdAt)}
                  </Typography>
                </div>
              ))
            )}
          </div>
        ) : messages.length === 0 ? (
          <div className={classes.welcomeSection}>
            <Typography className={classes.welcomeText}>Comment puis-je vous aider ?</Typography>
            <div className={classes.predefinedQueriesContainer}>
              {predefinedQueries.map(query => {
                const [title, subtitle] = query.split('\n');
                return (
                  <button
                    key={query}
                    type="button"
                    className={classes.predefinedQueryButton}
                    onClick={() => onSelectPredefinedQuery(query)}
                    disabled={isLoading}>
                    <span className={classes.predefinedQueryTitle}>{title}</span>
                    <span className={classes.predefinedQuerySubtitle}>{subtitle}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className={classes.messagesList}>
            {messages.map((message, index) => {
              const isLastMessage = index === messages.length - 1;

              return (
                <div
                  key={message.id}
                  className={classes.messageContainer}
                  ref={isLastMessage ? lastMessageRef : null}>
                {message.sender === 'assistant' && (
                  <div className={classes.messageHeader}>
                    <img src={ImgIADrawer} alt="Votre coach Orientaction" style={{ width: '40px', height: '40px' }} />
                    <Typography className={classes.messageSender}>
                      Votre coach Orientaction
                    </Typography>
                    <Typography
                      className={classes.messageTime}
                      style={{ margin: '0 4px', fontSize: '16px' }}>
                      •
                    </Typography>
                    <Typography className={classes.messageTime}>
                      {formatTime(message.timestamp)}
                    </Typography>
                  </div>
                )}
                {message.sender === 'user' && (
                  <div
                    className={classes.messageHeader}
                    style={{ justifyContent: 'flex-end', alignSelf: 'flex-end' }}>
                    <Typography className={classes.messageSender}>Vous</Typography>
                    <Typography
                      className={classes.messageTime}
                      style={{ margin: '0', fontSize: '16px' }}>
                      •
                    </Typography>
                    <Typography className={classes.messageTime}>
                      {formatTime(message.timestamp)}
                    </Typography>
                  </div>
                )}
                <div
                  className={`${classes.messageBubble} ${message.sender === 'user' ? classes.userMessage : classes.assistantMessage
                    }`}>
                  {message.sender === 'assistant' ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        img: ({ node, ...props }) => (
                          <img
                            {...props}
                            crossOrigin="anonymous"
                            style={{
                              maxWidth: '100%',
                              height: 'auto',
                              borderRadius: '8px',
                              marginTop: '12px',
                              marginBottom: '12px',
                            }}
                            alt={props.alt || 'Image'}
                            onError={e => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ),
                        a: ({ node, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer">
                            {props.children}
                          </a>
                        ),
                      }}>
                      {message.text}
                    </ReactMarkdown>
                  ) : (
                    message.text.split('\n').map((line, i, array) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <span key={`${message.id}-line-${i}`}>
                        {line}
                        {i < array.length - 1 && <br />}
                      </span>
                    ))
                  )}
                </div>

                </div>
              );
            })}
            {isLoading && (
              <div className={classes.typingIndicator}>
                <span>•</span>
                <span>•</span>
                <span>•</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={classes.inputContainer}>
        <TextField
          fullWidth={true}
          variant="outlined"
          placeholder="Posez une question"
          className={classes.input}
          value={inputValue}
          onChange={e => onInputChange(e.target.value)}
          onKeyPress={handleKeyPress}
          multiline={true}
          maxRows={4}
          disabled={!messages || messages.length === 0}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  className={classes.sendButton}
                  onClick={onSendMessage}
                  disabled={!inputValue.trim() || isLoading}
                  size="small">
                  <ArrowUpwardIcon style={{ fontSize: 20 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>
    </Drawer>
  );
};

export default IADrawer;
